// Instagram Story Selection Modal - allows creators to select stories to submit

import type { InstagramStory } from "@/lib/queries/content";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@repo/ui";
import { ImageOff, Play, RefreshCw } from "lucide-react";
import { InstagramInsightsBanner } from "./instagram-insights-banner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ============================================================================
// Types
// ============================================================================

interface InstagramStoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stories: InstagramStory[];
  loading: boolean;
  brandInstagramUsername?: string | null;
  onSelectStories: (stories: InstagramStory[]) => void;
  onUpdateClick: () => void | Promise<void>;
  /** When true, shows a banner that Instagram Insights must be linked */
  insightsNotLinked?: boolean;
  /** Usernames of accounts that need Insights linked */
  unlinkedAccountUsernames?: string[];
}

interface StoryListItemProps {
  story: InstagramStory;
  checked: boolean;
  onClick: () => void;
}

// ============================================================================
// Story List Item Component
// ============================================================================

function StoryListItem({ story, checked, onClick }: StoryListItemProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <div
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onClick}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="relative w-[68px] h-[120px] rounded-md overflow-hidden bg-muted shrink-0">
        {!imageError && story.imageUrl ? (
          <img
            src={story.imageUrl}
            alt="Story thumbnail"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </div>
        )}
        {story.video && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Play className="h-6 w-6 text-white fill-white" />
          </div>
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium truncate">{story.date}</span>
        <span className="text-sm text-muted-foreground line-clamp-3">
          {story.description || "No caption"}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Loading Skeleton Component
// ============================================================================

function StoryLoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-8 w-12" />
        <Skeleton className="h-5 w-40" />
        <div className="flex gap-2 mt-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-36" />
        </div>
      </div>
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="w-[68px] h-[120px] rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InstagramStoryModal({
  open,
  onOpenChange,
  stories,
  loading,
  brandInstagramUsername,
  onSelectStories,
  onUpdateClick,
  insightsNotLinked = false,
  unlinkedAccountUsernames,
}: InstagramStoryModalProps) {
  const [selectedStoryKeys, setSelectedStoryKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [nextMentionIndex, setNextMentionIndex] = useState(0);
  const storyListRef = useRef<HTMLDivElement>(null);

  // Sort stories by posted time (newest first)
  const sortedStories = useMemo(() => {
    return [...stories].sort((a, b) => (b.postedTs ?? 0) - (a.postedTs ?? 0));
  }, [stories]);

  // Find indexes of stories that mention the brand
  const mentionIndexes = useMemo(() => {
    if (!brandInstagramUsername) return [];

    const brandMention = `@${brandInstagramUsername.toLowerCase()}`;
    const indexes: number[] = [];

    sortedStories.forEach((story, index) => {
      const text = (story.caption ?? story.text ?? "").toLowerCase();
      if (text.includes(brandMention)) {
        indexes.push(index);
      }
    });

    return indexes;
  }, [sortedStories, brandInstagramUsername]);

  // Reset selection when modal opens
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened - reset state
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- intentional reset on modal open
      setSelectedStoryKeys(new Set());
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- intentional reset on modal open
      setNextMentionIndex(0);
    }
    prevOpenRef.current = open;
  }, [open]);

  // Clamp nextMentionIndex when mentionIndexes changes to prevent out-of-bounds access
  useEffect(() => {
    if (nextMentionIndex >= mentionIndexes.length) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- intentional bounds check
      setNextMentionIndex(0);
    }
  }, [mentionIndexes, nextMentionIndex]);

  const handleItemClick = useCallback((storyKey: string) => {
    setSelectedStoryKeys((prev) => {
      const next = new Set(prev);
      if (next.has(storyKey)) {
        next.delete(storyKey);
      } else {
        next.add(storyKey);
      }
      return next;
    });
  }, []);

  const handleSelectStories = useCallback(() => {
    const selected = sortedStories.filter((story) =>
      selectedStoryKeys.has(story.key),
    );
    onSelectStories(selected);
    onOpenChange(false);
  }, [sortedStories, selectedStoryKeys, onSelectStories, onOpenChange]);

  const scrollToNextMention = useCallback(() => {
    const storyList = storyListRef.current;
    if (!storyList || mentionIndexes.length === 0) return;

    // Find the container div which contains the actual scrollable content
    const container = storyList.querySelector(".space-y-1");
    if (!container) return;

    const storyItems = container.querySelectorAll(":scope > div");
    const itemHeight = 136; // Approximate height of each story item

    // Find the next mention index that's not currently visible
    const scrollTop = storyList.scrollTop;
    const skippedItemCount = Math.floor(scrollTop / itemHeight);

    let index = nextMentionIndex;
    // Skip items that are already scrolled past (except when wrapping around)
    if (index > 0) {
      while (
        index < mentionIndexes.length - 1 &&
        mentionIndexes[index] < skippedItemCount
      ) {
        index++;
      }
    }

    const nextStoryItem = storyItems[mentionIndexes[index]];
    // Scroll the item into view within the container
    if (nextStoryItem) {
      const itemTop = (nextStoryItem as HTMLElement).offsetTop;
      storyList.scrollTo({
        top: itemTop,
        behavior: index === 0 ? "auto" : "smooth",
      });
    }

    // Wrap around to the beginning if at the end
    setNextMentionIndex(index === mentionIndexes.length - 1 ? 0 : index + 1);
  }, [mentionIndexes, nextMentionIndex]);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    try {
      await onUpdateClick();
    } finally {
      setIsUpdating(false);
    }
  }, [onUpdateClick]);

  const handleClose = useCallback(() => {
    setSelectedStoryKeys(new Set());
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Instagram Stories</DialogTitle>
          <DialogDescription>
            Choose the stories you want to submit to the brand.
          </DialogDescription>
        </DialogHeader>

        {insightsNotLinked && (
          <div className="pt-2">
            <InstagramInsightsBanner
              accountUsernames={unlinkedAccountUsernames}
            />
          </div>
        )}

        {loading ? (
          <StoryLoadingSkeleton />
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Selection count and action buttons */}
            <div className="flex flex-col items-center gap-2 py-4 border-b">
              <span className="text-3xl font-semibold">
                {selectedStoryKeys.size}
              </span>
              <span className="text-sm text-muted-foreground">
                Instagram Stories Selected
              </span>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUpdate}
                  disabled={isUpdating}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2${isUpdating ? " animate-spin" : ""}`}
                  />
                  {isUpdating ? "Updating..." : "Update Stories"}
                </Button>
                {mentionIndexes.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={scrollToNextMention}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Next Brand Mention
                  </Button>
                )}
              </div>
            </div>

            {/* Story list */}
            {sortedStories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <ImageOff className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No stories available. Try clicking "Update Stories" to
                  refresh.
                </p>
              </div>
            ) : (
              <div
                className="flex-1 min-h-0 -mx-6 px-6 overflow-y-auto"
                ref={storyListRef}
              >
                <div className="space-y-1 py-4">
                  {sortedStories.map((story) => (
                    <StoryListItem
                      key={story.key}
                      story={story}
                      checked={selectedStoryKeys.has(story.key)}
                      onClick={() => handleItemClick(story.key)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSelectStories}
            disabled={selectedStoryKeys.size === 0 || loading}
          >
            Select {selectedStoryKeys.size > 0 ? selectedStoryKeys.size : ""}{" "}
            {selectedStoryKeys.size === 1 ? "Story" : "Stories"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
